import * as THREE from "three";
import { bus } from "../core/bus";
import { terrainHeightAt } from "../world/terrain";

// Armor and vehicles: the tactical puzzle that forces weapon choice
// (docs/design/GDD.md §Enemies). Tanks shrug off the 25mm entirely, bleed
// under sustained 30mm, and die to the 105. Trucks are soft but deliver
// infantry where you don't want it. Infantry spawning stays in EnemyManager
// — this module reports deployments through a callback.

export type ArmorKind = "tank" | "truck";

export type ArmorUnitDef = {
  kind: ArmorKind;
  origin: [number, number, number];
  // Convoy mode: drive these waypoints instead of hunting the squad. A unit
  // that reaches the last waypoint has ESCAPED (AMBUSH fail pressure).
  path?: Array<[number, number, number]>;
};

export const TANK_HP = 100;
export const TRUCK_HP = 30;

// Damage matrix: [weaponId][kind]. Blast damage uses the full value through
// the inner radius and half at the rim; hitscan uses the direct value.
export function armorDamageFor(weaponId: string, kind: ArmorKind): number {
  if (kind === "tank") {
    if (weaponId === "howitzer") return 60;
    if (weaponId === "autocannon") return 12;
    return 0; // 25mm cannot hurt armor — the ricochet is the lesson
  }
  if (weaponId === "howitzer") return 30;
  if (weaponId === "autocannon") return 15;
  return 4;
}

const ACTIVATE_RADIUS = 320;
const TANK_ENGAGE_RANGE = 150;
const TANK_SPEED = 2.6;
const TANK_FIRE_INTERVAL = 6.5;
const TANK_HIT_CHANCE = 0.55;
const TRUCK_DEPLOY_RANGE = 95;
const TRUCK_SPEED = 6.5;
const TRUCK_DEPLOY_COUNT = 4;
const CREW_BAIL_COUNT = 2;

type ArmorState = "idle" | "advancing" | "engaging" | "dead" | "escaped";

type ArmorUnit = {
  def: ArmorUnitDef;
  mesh: THREE.Group;
  hp: number;
  state: ArmorState;
  fireTimer: number;
  deployed: boolean;
  pathIdx: number;
};

export type DeployCallback = (position: THREE.Vector3, count: number, posture: "assault" | "ambush") => void;

function buildTankMesh(): THREE.Group {
  const g = new THREE.Group();
  const hullMat = new THREE.MeshStandardMaterial({ color: 0x4c5240, roughness: 0.92, metalness: 0.12, flatShading: true });
  const trackMat = new THREE.MeshStandardMaterial({ color: 0x23241f, roughness: 1, flatShading: true });
  const hull = new THREE.Mesh(new THREE.BoxGeometry(3.4, 1.1, 6.2), hullMat);
  hull.position.y = 1.15;
  const turret = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.85, 2.6), hullMat);
  turret.position.y = 2.1;
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.17, 4.6, 6), trackMat);
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 2.15, 3.2);
  const trackL = new THREE.Mesh(new THREE.BoxGeometry(0.8, 1.0, 6.4), trackMat);
  trackL.position.set(-1.8, 0.6, 0);
  const trackR = trackL.clone();
  trackR.position.x = 1.8;
  g.add(hull, turret, barrel, trackL, trackR);
  return g;
}

function buildTruckMesh(): THREE.Group {
  const g = new THREE.Group();
  const bodyMat = new THREE.MeshStandardMaterial({ color: 0x5d5f52, roughness: 0.9, flatShading: true });
  const cabMat = new THREE.MeshStandardMaterial({ color: 0x43463f, roughness: 0.85, flatShading: true });
  const bed = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1.5, 4.4), bodyMat);
  bed.position.set(0, 1.45, -0.9);
  const cab = new THREE.Mesh(new THREE.BoxGeometry(2.3, 1.7, 1.8), cabMat);
  cab.position.set(0, 1.55, 2.1);
  const wheelMat = new THREE.MeshStandardMaterial({ color: 0x191919, roughness: 1 });
  for (const [x, z] of [[-1.2, 2.0], [1.2, 2.0], [-1.2, -1.6], [1.2, -1.6]] as const) {
    const w = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 0.4, 8), wheelMat);
    w.rotation.z = Math.PI / 2;
    w.position.set(x, 0.55, z);
    g.add(w);
  }
  g.add(bed, cab);
  return g;
}

const _toTarget = new THREE.Vector3();
const _from = new THREE.Vector3();

export class ArmorManager {
  readonly group = new THREE.Group();
  private readonly units: ArmorUnit[] = [];

  constructor(defs: ArmorUnitDef[], private readonly onDeploy: DeployCallback) {
    this.group.name = "armor-units";
    for (const def of defs) {
      const mesh = def.kind === "tank" ? buildTankMesh() : buildTruckMesh();
      mesh.position.set(def.origin[0], terrainHeightAt(def.origin[0], def.origin[2]), def.origin[2]);
      mesh.traverse((o) => {
        o.userData["isArmor"] = true;
        o.castShadow = true;
      });
      this.group.add(mesh);
      this.units.push({
        def,
        mesh,
        hp: def.kind === "tank" ? TANK_HP : TRUCK_HP,
        state: def.path ? "advancing" : "idle", // convoys are already rolling
        fireTimer: TANK_FIRE_INTERVAL * (0.5 + Math.random() * 0.5),
        deployed: false,
        pathIdx: 0,
      });
    }
  }

  get aliveCount(): number {
    return this.units.filter((u) => u.state !== "dead" && u.state !== "escaped").length;
  }

  get totalCount(): number {
    return this.units.length;
  }

  get escapedCount(): number {
    return this.units.filter((u) => u.state === "escaped").length;
  }

  alivePositions(): THREE.Vector3[] {
    return this.units
      .filter((u) => u.state !== "dead" && u.state !== "escaped")
      .map((u) => u.mesh.position);
  }

  update(dt: number, squadPositions: THREE.Vector3[]): void {
    for (const unit of this.units) {
      if (unit.state === "dead" || unit.state === "escaped") continue;

      // Convoy units follow their route regardless of the squad
      if (unit.def.path) {
        this.updateConvoyUnit(unit, dt);
        continue;
      }
      if (squadPositions.length === 0) continue;

      // Nearest squad member is the target
      let nearest = squadPositions[0]!;
      let best = Infinity;
      for (const p of squadPositions) {
        const d = unit.mesh.position.distanceTo(p);
        if (d < best) { best = d; nearest = p; }
      }

      if (unit.state === "idle") {
        if (best < ACTIVATE_RADIUS) unit.state = "advancing";
        else continue;
      }

      _toTarget.copy(nearest).sub(unit.mesh.position);
      _toTarget.y = 0;
      const dist = _toTarget.length();
      if (dist > 1) unit.mesh.rotation.y = Math.atan2(_toTarget.x, _toTarget.z);

      if (unit.def.kind === "truck") {
        if (!unit.deployed && dist <= TRUCK_DEPLOY_RANGE) {
          unit.deployed = true;
          unit.state = "engaging"; // parked
          this.onDeploy(unit.mesh.position.clone(), TRUCK_DEPLOY_COUNT, "assault");
          bus.emit("armor:deployed", { position: unit.mesh.position.clone() });
        } else if (!unit.deployed) {
          unit.mesh.position.addScaledVector(_toTarget.normalize(), TRUCK_SPEED * dt);
          unit.mesh.position.y = terrainHeightAt(unit.mesh.position.x, unit.mesh.position.z);
        }
        continue;
      }

      // Tank: close to gun range, then pound the squad
      if (dist > TANK_ENGAGE_RANGE) {
        unit.state = "advancing";
        unit.mesh.position.addScaledVector(_toTarget.normalize(), TANK_SPEED * dt);
        unit.mesh.position.y = terrainHeightAt(unit.mesh.position.x, unit.mesh.position.z);
      } else {
        unit.state = "engaging";
        unit.fireTimer -= dt;
        if (unit.fireTimer <= 0) {
          unit.fireTimer = TANK_FIRE_INTERVAL;
          _from.copy(unit.mesh.position);
          _from.y += 2.2;
          bus.emit("enemy:fired", { from: _from.clone(), to: nearest.clone(), hit: false });
          bus.emit("armor:tankFired", { hit: Math.random() < TANK_HIT_CHANCE });
        }
      }
    }
  }

  private updateConvoyUnit(unit: ArmorUnit, dt: number): void {
    const path = unit.def.path!;
    const target = path[unit.pathIdx];
    if (!target) {
      unit.state = "escaped";
      unit.mesh.visible = false;
      bus.emit("armor:escaped", { kind: unit.def.kind, position: unit.mesh.position.clone() });
      return;
    }
    _toTarget.set(target[0] - unit.mesh.position.x, 0, target[2] - unit.mesh.position.z);
    const dist = _toTarget.length();
    const speed = unit.def.kind === "truck" ? TRUCK_SPEED : TANK_SPEED * 1.8; // column keeps pace
    if (dist < 6) {
      unit.pathIdx++;
      return;
    }
    unit.mesh.rotation.y = Math.atan2(_toTarget.x, _toTarget.z);
    unit.mesh.position.addScaledVector(_toTarget.normalize(), speed * dt);
    unit.mesh.position.y = terrainHeightAt(unit.mesh.position.x, unit.mesh.position.z);
  }

  // Player ordnance. Blast: full matrix damage through 60% of the radius,
  // half at the rim. Returns kinds destroyed this call.
  hitInRadius(center: THREE.Vector3, radius: number, weaponId: string): ArmorKind[] {
    const destroyed: ArmorKind[] = [];
    for (const unit of this.units) {
      if (unit.state === "dead" || unit.state === "escaped") continue;
      const dist = unit.mesh.position.distanceTo(center);
      if (dist > radius + 2.5) continue;
      const base = armorDamageFor(weaponId, unit.def.kind);
      const dmg = dist <= (radius + 2.5) * 0.6 ? base : Math.floor(base / 2);
      if (dmg <= 0) {
        bus.emit("armor:ricochet", { position: unit.mesh.position.clone() });
        continue;
      }
      unit.hp -= dmg;
      if (unit.hp <= 0) {
        this.destroy(unit);
        destroyed.push(unit.def.kind);
      }
    }
    return destroyed;
  }

  // Direct hitscan hit (chaingun) on an armor mesh.
  hitObject(obj: THREE.Object3D, weaponId: string): "destroyed" | "hit" | "ricochet" | null {
    let node: THREE.Object3D | null = obj;
    while (node) {
      const unit = this.units.find((u) => u.mesh === node && u.state !== "dead" && u.state !== "escaped");
      if (unit) {
        const dmg = armorDamageFor(weaponId, unit.def.kind);
        if (dmg <= 0) {
          bus.emit("armor:ricochet", { position: unit.mesh.position.clone() });
          return "ricochet";
        }
        unit.hp -= dmg;
        if (unit.hp <= 0) {
          this.destroy(unit);
          return "destroyed";
        }
        return "hit";
      }
      node = node.parent;
    }
    return null;
  }

  private destroy(unit: ArmorUnit): void {
    unit.state = "dead";
    unit.mesh.visible = false;
    // Tanks: the crew bails and scatters. Trucks that hadn't unloaded yet
    // dump their infantry at the wreck. Convoy vehicles carry no deployable
    // squads — their cargo dies with them.
    if (!unit.def.path) {
      if (unit.def.kind === "tank") {
        this.onDeploy(unit.mesh.position.clone(), CREW_BAIL_COUNT, "ambush");
      } else if (!unit.deployed) {
        unit.deployed = true;
        this.onDeploy(unit.mesh.position.clone(), TRUCK_DEPLOY_COUNT, "assault");
      }
    }
    bus.emit("armor:destroyed", { kind: unit.def.kind, position: unit.mesh.position.clone() });
  }

  dispose(): void {
    this.group.removeFromParent();
  }
}
